import moment from "moment";
import { ParticipantsList, RoomAttendanceTime } from "../../../types/common";
import TablePagination from "../Pagination";
import UseTable from "../useTable";
import { useState, useEffect } from "react";
import { getAllRoomAttendanceData } from "../../../hooks/useAddRoomParticipants";
import * as XLSX from "xlsx";
import { saveAs } from "file-saver";

const AttendanceDataTable = ({
  data,
}: {
  data: ParticipantsList[] | undefined;
  setExcelDataRefined?: React.Dispatch<
    React.SetStateAction<ParticipantsList[] | undefined>
  >;
}) => {
  const { page, rowsPerPage, handlePageChange, handleRowsPerPage } = UseTable();
  const [roomAttendanceData, setRoomAttendanceData] = useState<{
    [key: string]: RoomAttendanceTime;
  }>({});
  const [loading, setLoading] = useState<boolean>(false);
  const [cacheLoaded, setCacheLoaded] = useState<boolean>(false);

  // Fetch all room attendance data once and cache it
  useEffect(() => {
    const fetchAllRoomAttendanceData = async () => {
      if (!cacheLoaded) {
        setLoading(true);
        try {
          const allRoomAttendanceMap = await getAllRoomAttendanceData();

          // Convert Map to object for easier access in the component
          const roomAttendanceObj: { [key: string]: RoomAttendanceTime } = {};
          allRoomAttendanceMap.forEach((value, key) => {
            roomAttendanceObj[key] = value;
          });
          setRoomAttendanceData(roomAttendanceObj);
          setCacheLoaded(true);
        } catch (error) {
          console.error("Error fetching all room attendance data:", error);
        } finally {
          setLoading(false);
        }
      }
    };

    fetchAllRoomAttendanceData();
  }, [cacheLoaded]);

  // Format with full date and time
  const getClockinDate = (date: any) => {
    if (!date) return "Not Clocked In";

    try {
      // Handle Firebase timestamp (with seconds property)
      if (typeof date === "object" && date.seconds) {
        return moment(new Date(date.seconds * 1000)).format(
          "YYYY/MM/DD hh:mm A"
        );
      }

      // Handle string timestamp
      if (typeof date === "string" && date) {
        return moment(new Date(date)).format("YYYY/MM/DD hh:mm A");
      }

      return "Not Clocked In";
    } catch (error) {
      console.error("Error formatting date:", error);
      return "Invalid Date";
    }
  };

  // Format with time only (no date) for room attendance
  const getTimeOnly = (date: any) => {
    if (!date) return "-";

    try {
      // Handle Firebase timestamp (with seconds property)
      if (typeof date === "object" && date.seconds) {
        return moment(new Date(date.seconds * 1000)).format("hh:mm A");
      }

      // Handle string timestamp
      if (typeof date === "string" && date) {
        return moment(new Date(date)).format("hh:mm A");
      }

      return "-";
    } catch (error) {
      console.error("Error formatting time:", error);
      return "Invalid Time";
    }
  };

  // Track if all attendances are being loaded
  const [initialLoading, setInitialLoading] = useState(true);
  const [exportLoading, setExportLoading] = useState(false);

  // Initialize data loading
  useEffect(() => {
    if (data && data.length > 0) {
      setInitialLoading(false);
    }
  }, [data]);

  // Export attendance data to Excel
  const exportToExcel = async () => {
    if (!data || data.length === 0) {
      alert("No data to export");
      return;
    }

    setExportLoading(true);
    try {
      // Create a complete dataset with room attendance data
      const exportData = [];

      // Create a copy of the complete attendance data
      const allParticipants = [...data];

      // Show progress message
      console.log(`Preparing to export ${allParticipants.length} records...`);

      // Get all room attendance data once if not already loaded
      let allRoomData = roomAttendanceData;
      if (Object.keys(roomAttendanceData).length === 0) {
        console.log("Fetching all room attendance data for export...");
        const allRoomAttendanceMap = await getAllRoomAttendanceData();
        allRoomData = {};
        allRoomAttendanceMap.forEach((value, key) => {
          allRoomData[key] = value;
        });
      }

      // Process all participants, not just the current page
      for (const participant of allParticipants) {
        if (participant.id) {
          // Get room attendance data from cache
          const attendanceData = allRoomData[participant.id] || {
            roomOneDayOne: "",
            roomTwoDayOne: "",
            roomThreeDayOne: "",
            roomOneDayTwo: "",
            roomTwoDayTwo: "",
            roomThreeDayTwo: "",
          };

          // Format data for export
          const rowData = {
            Name: participant.name || "",
            Email: participant.email || "",
            Phone: participant.phone || "",
            "Designation": participant.dealershipName || "",
            Team: participant.district || "",
            "Day 1 Check-in": participant.clockedInDayOne
              ? getClockinDate(participant.clockedInDayOne)
              : "Not Clocked In",
            "Day 1 - Room 1": attendanceData?.roomOneDayOne
              ? getTimeOnly(attendanceData.roomOneDayOne)
              : "-",
            "Day 1 - Room 2": attendanceData?.roomTwoDayOne
              ? getTimeOnly(attendanceData.roomTwoDayOne)
              : "-",
            "Day 1 - Room 3": attendanceData?.roomThreeDayOne
              ? getTimeOnly(attendanceData.roomThreeDayOne)
              : "-",
            "Day 2 Check-in": participant.clockedInDayTwo
              ? getClockinDate(participant.clockedInDayTwo)
              : "Not Clocked In",
            "Day 2 - Room 1": attendanceData?.roomOneDayTwo
              ? getTimeOnly(attendanceData.roomOneDayTwo)
              : "-",
            "Day 2 - Room 2": attendanceData?.roomTwoDayTwo
              ? getTimeOnly(attendanceData.roomTwoDayTwo)
              : "-",
            "Day 2 - Room 3": attendanceData?.roomThreeDayTwo
              ? getTimeOnly(attendanceData.roomThreeDayTwo)
              : "-",
          };

          exportData.push(rowData);
        }
      }

      // Create worksheet
      const worksheet = XLSX.utils.json_to_sheet(exportData);

      // Set column widths for better readability
      const colWidths = [
        { wch: 20 }, // Name
        { wch: 30 }, // Email
        { wch: 15 }, // Phone
        { wch: 25 }, // Dealership Name
        { wch: 15 }, // District
        { wch: 22 }, // Day 1 Check-in
        { wch: 15 }, // Day 1 - Room 1
        { wch: 15 }, // Day 1 - Room 2
        { wch: 15 }, // Day 1 - Room 3
        { wch: 22 }, // Day 2 Check-in
        { wch: 15 }, // Day 2 - Room 1
        { wch: 15 }, // Day 2 - Room 2
        { wch: 15 }, // Day 2 - Room 3
      ];
      worksheet["!cols"] = colWidths;

      // Create workbook
      const workbook = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(workbook, worksheet, "Attendance");

      // Add title and date in another sheet for metadata
      const metaSheet = XLSX.utils.aoa_to_sheet([
        ["CAS2025 Attendance Report"],
        [`Generated on: ${moment().format("MMMM D, YYYY, h:mm A")}`],
        [`Participants: ${exportData.length}`],
        [],
      ]);
      XLSX.utils.book_append_sheet(workbook, metaSheet, "Report Info");

      // Generate Excel file
      const excelBuffer = XLSX.write(workbook, {
        bookType: "xlsx",
        type: "array",
      });
      const fileData = new Blob([excelBuffer], {
        type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
      });

      // Save file
      saveAs(
        fileData,
        `CAS2025_Attendance_Report_${moment().format("YYYY-MM-DD")}.xlsx`
      );
    } catch (error) {
      console.error("Error exporting to Excel:", error);
      alert("Failed to export data. Please try again.");
    } finally {
      setExportLoading(false);
    }
  };

  return (
    <div>
      {initialLoading ? (
        <div className="flex justify-center items-center p-10">
          <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-blue-500"></div>
        </div>
      ) : (
        <div className="relative overflow-x-auto shadow-md sm:rounded-lg">
          {/* Export Button */}
          <div className="flex justify-end mb-4 px-4">
            <button
              onClick={exportToExcel}
              disabled={exportLoading}
              className="flex items-center px-4 py-2 bg-green-600 text-white rounded-md hover:bg-green-700 transition duration-200 shadow-md"
            >
              {exportLoading ? (
                <>
                  <svg
                    className="animate-spin -ml-1 mr-2 h-4 w-4 text-white"
                    xmlns="http://www.w3.org/2000/svg"
                    fill="none"
                    viewBox="0 0 24 24"
                  >
                    <circle
                      className="opacity-25"
                      cx="12"
                      cy="12"
                      r="10"
                      stroke="currentColor"
                      strokeWidth="4"
                    ></circle>
                    <path
                      className="opacity-75"
                      fill="currentColor"
                      d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                    ></path>
                  </svg>
                  Exporting...
                </>
              ) : (
                <>
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    fill="none"
                    viewBox="0 0 24 24"
                    strokeWidth={1.5}
                    stroke="currentColor"
                    className="w-5 h-5 mr-2"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      d="M3 16.5v2.25A2.25 2.25 0 0 0 5.25 21h13.5A2.25 2.25 0 0 0 21 18.75V16.5m-13.5-9L12 3m0 0 4.5 4.5M12 3v13.5"
                    />
                  </svg>
                  Export Table
                </>
              )}
            </button>
          </div>

          <table className="w-full text-sm text-left rtl:text-right text-gray-500">
            <thead className="text-xs text-gray-700 uppercase bg-gray-50">
              {/* Main header row with day groupings */}
              <tr>
                <th scope="col" className="px-6 py-3 border-r">
                  Name
                </th>
                <th scope="col" className="px-6 py-3 border-r">
                  Designation
                </th>
                <th scope="col" className="px-6 py-3 border-r">
                  Team
                </th>

                {/* Day 1 headers */}
                <th scope="col" className="px-6 py-3 bg-indigo-50 border-r">
                  Day 1 Overall
                </th>
                <th
                  scope="col"
                  colSpan={3}
                  className="px-6 py-3 text-center bg-blue-50 border-r"
                >
                  Day 1 Rooms
                </th>

                {/* Day 2 headers */}
                <th scope="col" className="px-6 py-3 bg-purple-50 border-r">
                  Day 2 Overall
                </th>
                <th
                  scope="col"
                  colSpan={3}
                  className="px-6 py-3 text-center bg-green-50"
                >
                  Day 2 Rooms
                </th>
              </tr>

              {/* Sub-header row with room numbers */}
              <tr>
                <th scope="col" className="px-6 py-2 border-r"></th>
                <th scope="col" className="px-6 py-2 border-r"></th>
                <th scope="col" className="px-6 py-2 border-r"></th>

                {/* Day 1 headers - sub row */}
                <th
                  scope="col"
                  className="px-6 py-2 bg-indigo-50 text-indigo-700 border-r"
                >
                  Check-in Time
                </th>

                {/* Day 1 Room Headers */}
                <th
                  scope="col"
                  className="px-4 py-2 bg-blue-50 text-blue-700 border-r"
                >
                  Room 1
                </th>
                <th
                  scope="col"
                  className="px-4 py-2 bg-blue-50 text-blue-700 border-r"
                >
                  Room 2
                </th>
                <th
                  scope="col"
                  className="px-4 py-2 bg-blue-50 text-blue-700 border-r"
                >
                  Room 3
                </th>

                {/* Day 2 headers - sub row */}
                <th
                  scope="col"
                  className="px-6 py-2 bg-purple-50 text-purple-700 border-r"
                >
                  Check-in Time
                </th>

                {/* Day 2 Room Headers */}
                <th
                  scope="col"
                  className="px-4 py-2 bg-green-50 text-green-700 border-r"
                >
                  Room 1
                </th>
                <th
                  scope="col"
                  className="px-4 py-2 bg-green-50 text-green-700 border-r"
                >
                  Room 2
                </th>
                <th
                  scope="col"
                  className="px-4 py-2 bg-green-50 text-green-700"
                >
                  Room 3
                </th>
              </tr>
            </thead>
            <tbody>
              {data
                ?.slice(
                  (page - 1) * rowsPerPage,
                  (page - 1) * rowsPerPage + rowsPerPage
                )
                .map((item, index) => {
                  const attendanceData = item.id
                    ? roomAttendanceData[item.id]
                    : null;

                  return (
                    <tr
                      key={index}
                      className={`group border-b hover:bg-gray-50`}
                    >
                      <th
                        scope="row"
                        className="px-6 py-4 font-medium text-gray-900 whitespace-nowrap border-r"
                      >
                        {item.name}
                      </th>
                      <td
                        className={`px-6 py-4 group-hover:text-gray-950 border-r`}
                      >
                        {item.dealershipName}
                      </td>
                      <td
                        className={`px-6 py-4 group-hover:text-gray-950 border-r`}
                      >
                        {item.district}
                      </td>

                      {/* Day 1 clockedIn time */}
                      <td className="px-6 py-4 border-r">
                        {item.clockedInDayOne ? (
                          <div className="inline-flex items-center space-x-1 bg-indigo-100 text-indigo-600 rounded-md py-1 px-2">
                            <svg
                              xmlns="http://www.w3.org/2000/svg"
                              fill="none"
                              viewBox="0 0 24 24"
                              strokeWidth={1.5}
                              stroke="currentColor"
                              className="size-5"
                            >
                              <path
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                d="M12 6v6h4.5m4.5 0a9 9 0 1 1-18 0 9 9 0 0 1 18 0Z"
                              />
                            </svg>
                            <div>{getClockinDate(item.clockedInDayOne)}</div>
                          </div>
                        ) : (
                          <div className="inline-flex items-center space-x-1 bg-yellow-100 text-yellow-600 py-1 px-2 rounded-md">
                            Not Clocked In
                          </div>
                        )}
                      </td>

                      {/* Day 1 - Room 1 */}
                      <td className="p-2 text-center border-r">
                        {loading ? (
                          <div className="flex justify-center">
                            <div className="animate-pulse bg-blue-100 h-6 w-20 rounded"></div>
                          </div>
                        ) : attendanceData?.roomOneDayOne ? (
                          <div className="inline-flex items-center space-x-1 bg-blue-100 text-blue-600 rounded-md py-1 px-2">
                            <svg
                              xmlns="http://www.w3.org/2000/svg"
                              fill="none"
                              viewBox="0 0 24 24"
                              strokeWidth={1.5}
                              stroke="currentColor"
                              className="size-4"
                            >
                              <path
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                d="M12 6v6h4.5m4.5 0a9 9 0 1 1-18 0 9 9 0 0 1 18 0Z"
                              />
                            </svg>
                            <div className="text-xs">
                              {getTimeOnly(attendanceData.roomOneDayOne)}
                            </div>
                          </div>
                        ) : (
                          <div className="text-xs text-gray-500">-</div>
                        )}
                      </td>

                      {/* Day 1 - Room 2 */}
                      <td className="p-2 text-center border-r">
                        {loading ? (
                          <div className="flex justify-center">
                            <div className="animate-pulse bg-blue-100 h-6 w-20 rounded"></div>
                          </div>
                        ) : attendanceData?.roomTwoDayOne ? (
                          <div className="inline-flex items-center space-x-1 bg-blue-100 text-blue-600 rounded-md py-1 px-2">
                            <svg
                              xmlns="http://www.w3.org/2000/svg"
                              fill="none"
                              viewBox="0 0 24 24"
                              strokeWidth={1.5}
                              stroke="currentColor"
                              className="size-4"
                            >
                              <path
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                d="M12 6v6h4.5m4.5 0a9 9 0 1 1-18 0 9 9 0 0 1 18 0Z"
                              />
                            </svg>
                            <div className="text-xs">
                              {getTimeOnly(attendanceData.roomTwoDayOne)}
                            </div>
                          </div>
                        ) : (
                          <div className="text-xs text-gray-500">-</div>
                        )}
                      </td>

                      {/* Day 1 - Room 3 */}
                      <td className="p-2 text-center border-r">
                        {loading ? (
                          <div className="flex justify-center">
                            <div className="animate-pulse bg-blue-100 h-6 w-20 rounded"></div>
                          </div>
                        ) : attendanceData?.roomThreeDayOne ? (
                          <div className="inline-flex items-center space-x-1 bg-blue-100 text-blue-600 rounded-md py-1 px-2">
                            <svg
                              xmlns="http://www.w3.org/2000/svg"
                              fill="none"
                              viewBox="0 0 24 24"
                              strokeWidth={1.5}
                              stroke="currentColor"
                              className="size-4"
                            >
                              <path
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                d="M12 6v6h4.5m4.5 0a9 9 0 1 1-18 0 9 9 0 0 1 18 0Z"
                              />
                            </svg>
                            <div className="text-xs">
                              {getTimeOnly(attendanceData.roomThreeDayOne)}
                            </div>
                          </div>
                        ) : (
                          <div className="text-xs text-gray-500">-</div>
                        )}
                      </td>

                      {/* Day 2 clockedIn time */}
                      <td className="px-6 py-4 border-r">
                        {item.clockedInDayTwo ? (
                          <div className="inline-flex items-center space-x-1 bg-purple-100 text-purple-600 rounded-md py-1 px-2">
                            <svg
                              xmlns="http://www.w3.org/2000/svg"
                              fill="none"
                              viewBox="0 0 24 24"
                              strokeWidth={1.5}
                              stroke="currentColor"
                              className="size-5"
                            >
                              <path
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                d="M12 6v6h4.5m4.5 0a9 9 0 1 1-18 0 9 9 0 0 1 18 0Z"
                              />
                            </svg>
                            <div>{getClockinDate(item.clockedInDayTwo)}</div>
                          </div>
                        ) : (
                          <div className="inline-flex items-center space-x-1 bg-yellow-100 text-yellow-600 py-1 px-2 rounded-md">
                            Not Clocked In
                          </div>
                        )}
                      </td>

                      {/* Day 2 - Room 1 */}
                      <td className="p-2 text-center border-r">
                        {loading ? (
                          <div className="flex justify-center">
                            <div className="animate-pulse bg-green-100 h-6 w-20 rounded"></div>
                          </div>
                        ) : attendanceData?.roomOneDayTwo ? (
                          <div className="inline-flex items-center space-x-1 bg-green-100 text-green-600 rounded-md py-1 px-2">
                            <svg
                              xmlns="http://www.w3.org/2000/svg"
                              fill="none"
                              viewBox="0 0 24 24"
                              strokeWidth={1.5}
                              stroke="currentColor"
                              className="size-4"
                            >
                              <path
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                d="M12 6v6h4.5m4.5 0a9 9 0 1 1-18 0 9 9 0 0 1 18 0Z"
                              />
                            </svg>
                            <div className="text-xs">
                              {getTimeOnly(attendanceData.roomOneDayTwo)}
                            </div>
                          </div>
                        ) : (
                          <div className="text-xs text-gray-500">-</div>
                        )}
                      </td>

                      {/* Day 2 - Room 2 */}
                      <td className="p-2 text-center border-r">
                        {loading ? (
                          <div className="flex justify-center">
                            <div className="animate-pulse bg-green-100 h-6 w-20 rounded"></div>
                          </div>
                        ) : attendanceData?.roomTwoDayTwo ? (
                          <div className="inline-flex items-center space-x-1 bg-green-100 text-green-600 rounded-md py-1 px-2">
                            <svg
                              xmlns="http://www.w3.org/2000/svg"
                              fill="none"
                              viewBox="0 0 24 24"
                              strokeWidth={1.5}
                              stroke="currentColor"
                              className="size-4"
                            >
                              <path
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                d="M12 6v6h4.5m4.5 0a9 9 0 1 1-18 0 9 9 0 0 1 18 0Z"
                              />
                            </svg>
                            <div className="text-xs">
                              {getTimeOnly(attendanceData.roomTwoDayTwo)}
                            </div>
                          </div>
                        ) : (
                          <div className="text-xs text-gray-500">-</div>
                        )}
                      </td>

                      {/* Day 2 - Room 3 */}
                      <td className="p-2 text-center">
                        {loading ? (
                          <div className="flex justify-center">
                            <div className="animate-pulse bg-green-100 h-6 w-20 rounded"></div>
                          </div>
                        ) : attendanceData?.roomThreeDayTwo ? (
                          <div className="inline-flex items-center space-x-1 bg-green-100 text-green-600 rounded-md py-1 px-2">
                            <svg
                              xmlns="http://www.w3.org/2000/svg"
                              fill="none"
                              viewBox="0 0 24 24"
                              strokeWidth={1.5}
                              stroke="currentColor"
                              className="size-4"
                            >
                              <path
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                d="M12 6v6h4.5m4.5 0a9 9 0 1 1-18 0 9 9 0 0 1 18 0Z"
                              />
                            </svg>
                            <div className="text-xs">
                              {getTimeOnly(attendanceData.roomThreeDayTwo)}
                            </div>
                          </div>
                        ) : (
                          <div className="text-xs text-gray-500">-</div>
                        )}
                      </td>
                    </tr>
                  );
                })}
            </tbody>
          </table>

          <TablePagination
            count={data?.length || 0}
            page={page}
            rowsPerPage={rowsPerPage}
            handlePageChange={handlePageChange}
            handleRowsPerPage={handleRowsPerPage}
          />
        </div>
      )}
    </div>
  );
};

export default AttendanceDataTable;
